import { ChatProvider } from "@/contexts/chatContext";
import { useQuery } from "@apollo/client/react";
import { Badge, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon } from "@repo/components/src/ui/button";
import { UnreadBrandChatsDocument } from "@repo/graphql/documents/chat/query.generated";
import { MessageCircle, X } from "lucide-react-native";
import {
  ComponentRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import FloatingChatPanel from "../../chat/floating/panel";
import { FloatingChatRef, FloatingChatShowParams } from "./types";

type Props = {
  brandId: string;
};

const PANEL_WIDTH = 480;
const PANEL_HEIGHT = 750;
const PANEL_GAP = 12;
const ANIMATION_MS = 200;

const FloatingChatRoot = forwardRef<FloatingChatRef, Props>(
  function FloatingChatRoot(props, ref) {
    const { brandId } = props;
    const [isOpen, setIsOpen] = useState(false);
    // `isMounted` keeps the panel in the tree during its exit transition so the
    // fade-out can play; `isEntered` toggles the CSS classes that animate it in.
    const [isMounted, setIsMounted] = useState(false);
    const [isEntered, setIsEntered] = useState(false);
    const [initialChatId, setInitialChatId] = useState<string | null>(null);
    const buttonRef = useRef<ComponentRef<typeof Button>>(null);
    const rafRef = useRef(0);
    const [panelLayout, setPanelLayout] = useState<{
      bottom: number;
      right: number;
      width: number;
      height: number;
    } | null>(null);

    const { data } = useQuery(UnreadBrandChatsDocument, {
      variables: {
        brand_Id: brandId,
        unreadByBrand_Gte: 1,
      },
    });

    const unreadCount = data?.chat.chats.totalCount ?? 0;

    const show = useCallback((params: FloatingChatShowParams) => {
      setInitialChatId(params.chatId);
      setIsOpen(true);
    }, []);

    const hide = useCallback(() => {
      setIsOpen(false);
    }, []);

    const toggle = useCallback(() => {
      setIsOpen((prev) => !prev);
    }, []);

    useImperativeHandle(
      ref,
      useCallback(() => ({ show, hide, toggle }), [show, hide, toggle]),
    );

    // Drive mount/unmount around the CSS transition.
    useEffect(() => {
      if (isOpen) {
        setIsMounted(true);
        return;
      }
      setIsEntered(false);
      const timeout = setTimeout(() => {
        setIsMounted(false);
        setPanelLayout(null);
        setInitialChatId(null);
      }, ANIMATION_MS);
      return () => clearTimeout(timeout);
    }, [isOpen]);

    // Position the panel relative to the launcher button before it paints.
    useLayoutEffect(() => {
      if (!isMounted || !buttonRef.current) return;
      const node = buttonRef.current as unknown as HTMLElement;
      const computeLayout = () => {
        const rect = node.getBoundingClientRect();
        const bottom = window.innerHeight - rect.top + PANEL_GAP;
        const right = window.innerWidth - rect.right;
        const width = Math.min(PANEL_WIDTH, window.innerWidth - 32);
        const height = Math.min(PANEL_HEIGHT, window.innerHeight - 96);
        setPanelLayout({ bottom, right, width, height });
      };
      computeLayout();
      window.addEventListener("resize", computeLayout);
      return () => window.removeEventListener("resize", computeLayout);
    }, [isMounted]);

    // Flip to the "entered" classes on the next frame so the transition runs
    // from the closed state instead of snapping straight to open.
    useEffect(() => {
      if (!isMounted) return;
      const outer = requestAnimationFrame(() => {
        const inner = requestAnimationFrame(() => setIsEntered(true));
        rafRef.current = inner;
      });
      rafRef.current = outer;
      return () => cancelAnimationFrame(rafRef.current);
    }, [isMounted]);

    return (
      <>
        {isMounted && panelLayout && (
          <ChatProvider initialChatId={initialChatId}>
            <Box
              className={`z-50 rounded-2xl overflow-hidden shadow-hard-2 transition-all duration-200 ease-out ${
                isEntered
                  ? "opacity-100 translate-y-0 scale-100"
                  : "opacity-0 translate-y-4 scale-95"
              }`}
              style={{
                position: "fixed",
                bottom: panelLayout.bottom,
                right: panelLayout.right,
                width: panelLayout.width,
                height: panelLayout.height,
                transformOrigin: "bottom right",
              }}
            >
              <FloatingChatPanel brandId={brandId} onClose={toggle} />
            </Box>
          </ChatProvider>
        )}
        <Box
          className="z-50 fixed"
          style={{
            bottom: 20,
            right: 80,
          }}
        >
          <Button
            onPress={toggle}
            action="brand"
            rounded="full"
            className="h-12 w-12 p-0 shadow-hard-3 transition-transform duration-200 ease-out data-[hover=true]:data-[active=false]:scale-110 data-[active=true]:scale-90"
            ref={buttonRef}
          >
            <ButtonIcon as={isOpen ? X : MessageCircle} className="h-5 w-5" />
            {!isOpen && unreadCount > 0 && (
              <Badge
                action="error"
                size="sm"
                rounded="full"
                className="absolute -top-1 -right-1"
              >
                <BadgeText>{unreadCount > 99 ? "99+" : unreadCount}</BadgeText>
              </Badge>
            )}
          </Button>
        </Box>
      </>
    );
  },
);

export default FloatingChatRoot;
